import React, { useEffect, useMemo, useState } from 'react';
import { db } from '../firebase/config';
import { collection, getDocs, query, orderBy, limit } from 'firebase/firestore';

interface LogEntry {
  id: string;
  level?: string;
  message?: string;
  user?: string;
  createdAt?: string;
}

const PAGE_SIZE = 50;

const LogsSection: React.FC = () => {
  const [logsAll, setLogsAll] = useState<LogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [levelFilter, setLevelFilter] = useState('all');
  const [userFilter, setUserFilter] = useState('');
  const [fromDate, setFromDate] = useState<string>('');
  const [toDate, setToDate] = useState<string>('');
  const [permissionsError, setPermissionsError] = useState<string | null>(null);
  const permLoggedRef = React.useRef(false);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      // Busca até 1000 registros recentes (client-side paging/filtragem)
      const q = query(collection(db, 'admin_logs'), orderBy('createdAt', 'desc'), limit(1000));
      const snapshot = await getDocs(q);
      const list: LogEntry[] = snapshot.docs.map(d => ({ id: d.id, ...(d.data() as any) }));
      setLogsAll(list);
      setPage(0);
    } catch (err: any) {
      // Log permission errors only once to avoid console spam
      if (err && (err.code === 'permission-denied' || String(err.message).includes('Missing or insufficient permissions'))) {
        if (!permLoggedRef.current) {
          console.error('Erro ao buscar logs (permission):', err);
          permLoggedRef.current = true;
        }
        setPermissionsError('Usuário sem permissão para acessar logs administrativos. Atualize suas permissões (verificar claims ou fazer logout/login).');
      } else {
        if (!permLoggedRef.current) console.error('Erro ao buscar logs:', err);
        setPermissionsError(null);
      }
    }
    setLoading(false);
  };

  useEffect(() => { fetchLogs(); }, []);

  const filtered = useMemo(() => {
    const f = logsAll.filter(l => {
      if (levelFilter !== 'all' && (l.level || '').toLowerCase() !== levelFilter) return false;
      if (userFilter && !(l.user || '').toLowerCase().includes(userFilter.toLowerCase())) return false;
      if (fromDate) {
        const from = new Date(fromDate);
        const created = l.createdAt ? new Date(l.createdAt) : null;
        if (!created || created < from) return false;
      }
      if (toDate) {
        const to = new Date(toDate);
        const created = l.createdAt ? new Date(l.createdAt) : null;
        if (!created || created > to) return false;
      }
      return true;
    });
    return f;
  }, [logsAll, levelFilter, userFilter, fromDate, toDate]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPageLogs = filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  const gotoPage = (p: number) => setPage(Math.min(Math.max(0, p), totalPages - 1));

  return (
    <div className="section-block">
      <h3>Logs e Histórico</h3>
      <p className="muted">Os registros abaixo vêm da coleção <code>admin_logs</code>. Atualize para ver registros recentes.</p>
      <div style={{ display: 'flex', gap: 12, marginBottom: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <div>
          <label className="muted">Nível: </label>
          <select aria-label="Filtrar por nível" value={levelFilter} onChange={e => { setLevelFilter(e.target.value); setPage(0); }}>
            <option value="all">Todos</option>
            <option value="info">Info</option>
            <option value="warning">Warning</option>
            <option value="error">Error</option>
            <option value="debug">Debug</option>
          </select>
        </div>

        {permissionsError && (
          <div style={{ background: '#fff3cd', border: '1px solid #ffeeba', padding: 12, borderRadius: 8 }}>
            <strong>Permissões insuficientes:</strong>
            <div style={{ marginTop: 6 }}>{permissionsError}</div>
            <div style={{ marginTop: 8 }}>
              <button onClick={async () => {
                try {
                  await (await import('firebase/auth')).getAuth().currentUser?.getIdToken(true);
                  alert('Token atualizado. Recarregue a página ou clique em Atualizar.');
                } catch (e) { console.warn('Erro ao atualizar token:', e); alert('Não foi possível atualizar o token automaticamente. Faça logout/login.'); }
              }}>Atualizar permissões</button>
            </div>
          </div>
        )}


        <div>
          <label className="muted">Usuário: </label>
          <input aria-label="Filtrar por usuário" placeholder="Pesquisar por usuário" value={userFilter} onChange={e => { setUserFilter(e.target.value); setPage(0); }} />
        </div>

        <div>
          <label className="muted">De: </label>
          <input type="date" aria-label="Filtrar de data" value={fromDate} onChange={e => { setFromDate(e.target.value); setPage(0); }} />
        </div>

        <div>
          <label className="muted">Até: </label>
          <input type="date" aria-label="Filtrar até data" value={toDate} onChange={e => { setToDate(e.target.value); setPage(0); }} />
        </div>

        <div>
          <button onClick={fetchLogs}>Atualizar</button>
        </div>

        <div className="muted">{filtered.length} registros</div>
      </div>

      {loading ? <p>Carregando logs...</p> : (
        filtered.length === 0 ? <p>Nenhum log encontrado.</p> : (
          <>
            <table className="admin-table compact" aria-label="Tabela de logs">
              <thead>
                <tr>
                  <th>Data</th>
                  <th>Nível</th>
                  <th>Mensagem</th>
                  <th>Usuário</th>
                </tr>
              </thead>
              <tbody>
                {currentPageLogs.map(l => (
                  <tr key={l.id}>
                    <td>{l.createdAt ? new Date(l.createdAt).toLocaleString('pt-BR') : '-'}</td>
                    <td>{l.level || '-'}</td>
                    <td style={{ maxWidth: 600 }}>{l.message || '-'}</td>
                    <td>{l.user || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12 }}>
              <div>
                Página {page + 1} de {totalPages}
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={() => gotoPage(0)} disabled={page === 0}>Primeira</button>
                <button onClick={() => gotoPage(page - 1)} disabled={page === 0}>Anterior</button>
                <button onClick={() => gotoPage(page + 1)} disabled={page >= totalPages - 1}>Próxima</button>
                <button onClick={() => gotoPage(totalPages - 1)} disabled={page >= totalPages - 1}>Última</button>
              </div>
            </div>
          </>
        )
      )}
    </div>
  );
};

export default LogsSection;